/* =============================================================================
   CORE / OPERATOR-SESSION — Operator-Layer Client-State (S125, Phase B)
   =============================================================================
   Single-Source für den aktuell am Gerät angemeldeten Operator („wer macht
   gerade was"). Liegt ÜBER der Supabase-Auth-Session (= Geräte-Session): das
   Auth-Konto bleibt, der Operator wechselt per Name+PIN ohne Re-Login.

   KERN-KONSEQUENZ Multi-Device (Konzept docs/s124-operator-layer-konzept.md):
   Alle Geräte können unter DEMSELBEN auth.uid() laufen → der Geräte-Kontext
   kommt IMMER explizit als `device_id` (client-generierte UUID in localStorage),
   nie aus der Auth-Session abgeleitet.

   RPC-Verträge (phase_90/phase_91):
     - operator_login(p_device_id,p_user_id,p_pin) → {ok:false,error:…} ODER
       {ok:true,session_id,operator_*}. IMMER `ok` prüfen, NICHT try/catch —
       nur KEIN_TENANT (kein Auth) wirft (kommt hier als rpc-error an).
     - operator_logout(p_device_id,p_reason 'logout'|'idle')
     - register_device(p_device_id,p_label,p_idle_timeout_minutes)

   Reine Helper (decideOnboarding / operatorErrorMessage / formatLockMessage)
   sind exportiert + Vitest-getestet. DB-/Realtime-Pfade hängen an
   window.App.supabase.
   Verwandte Memories: feedback_gg_db_for_critical_data, feedback_gg_no_view_hijack.
============================================================================ */

const DEVICE_ID_KEY = 'gg_device_id';

let _deviceId = null;          // memoisierte client-UUID
let _device = null;            // { id, label, idle_timeout_minutes } | null (unregistriert)
let _operator = null;          // { sessionId, userId, name, initials, role, roles[], deviceId, startedAt } | null
let _realtimeChannel = null;

/* -------------------------------------------------------------------------- */
/* Internals                                                                   */
/* -------------------------------------------------------------------------- */

function getSupa() {
  return (typeof window !== 'undefined' && window.App && window.App.supabase) || null;
}

function emitChanged() {
  const App = typeof window !== 'undefined' && window.App;
  if (App && App.events && typeof App.events.emit === 'function') {
    try { App.events.emit('operator:changed', getOperator()); } catch (_) { /* ignore */ }
  }
}

function _genUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback (sehr alte Browser/Test-Umgebung) — kein Krypto-Garant nötig, die
  // device_id ist nur ein Identifier, kein Secret.
  return 'dev-' + Date.now().toString(16) + '-' + Math.random().toString(16).slice(2, 10);
}

function _setOperator(op) {
  const changed = JSON.stringify(op || null) !== JSON.stringify(_operator || null);
  _operator = op || null;
  if (changed) emitChanged();
}

/* -------------------------------------------------------------------------- */
/* Pure Helpers (Vitest)                                                       */
/* -------------------------------------------------------------------------- */

/** Onboarding nötig? → wenn kein registriertes Gerät existiert. */
export function decideOnboarding(deviceRow) {
  return !deviceRow;
}

/** Erkennt den strict-Enforcement-Reject des BEFORE-Triggers (S129/phase_94).
 *  Wahr für Fehler, die der Server mit 'OPERATOR_REQUIRED' meldet, bzw. für die
 *  vom Storage-Adapter getaggten `operatorRequired`-Fehler. Caller können damit
 *  statt eines kryptischen Toasts das PIN-Overlay öffnen. */
export function isOperatorRequiredError(err) {
  if (!err) return false;
  if (err.operatorRequired === true) return true;
  // PostgREST verpackt RPC- vs. Direkt-Write-Fehler unterschiedlich (message vs.
  // details/hint) → alle Felder scannen.
  const blob = [err.message, err.msg, err.details, err.hint, err.error_description]
    .map(x => String(x || '')).join(' ');
  return /OPERATOR_REQUIRED/.test(blob);
}

/** Erkennt den Rollen-Reject (S131/phase_97 assert_operator_role): der Operator
 *  IST angemeldet, hat aber für diese Aktion die falsche Rolle. Caller zeigen
 *  dann einen freundlichen Hinweis (KEIN PIN-Overlay — anmelden hilft nicht).
 *  Wichtig: zuerst isOperatorRequiredError() prüfen (kein Operator) und nur bei
 *  false hierher fallen, denn beide tragen den Präfix „OPERATOR_". */
export function isOperatorForbiddenError(err) {
  if (!err) return false;
  if (err.operatorForbidden === true) return true;
  const blob = [err.message, err.msg, err.details, err.hint, err.error_description]
    .map(x => String(x || '')).join(' ');
  return /OPERATOR_FORBIDDEN/.test(blob);
}

/** Deutsche, freundliche Restzeit-Meldung für eine PIN-Sperre.
 *  S126 Befund 5: `round` statt `ceil` (sonst zeigt ein 5:00-Lock durch
 *  Client-Latenz „6 Minuten"). Unter 90 s → Sekunden, sonst Minuten gerundet. */
export function formatLockMessage(lockedUntil, nowMs) {
  const until = lockedUntil ? Date.parse(lockedUntil) : NaN;
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const diff = until - now;
  if (!Number.isFinite(until) || diff <= 0) {
    return 'Zu viele Fehlversuche. Bitte kurz warten und erneut versuchen.';
  }
  if (diff < 90000) {
    const secs = Math.max(5, Math.round(diff / 1000));
    return `Zu viele Fehlversuche. Gesperrt für noch ${secs} Sekunden.`;
  }
  const mins = Math.round(diff / 60000);
  return `Zu viele Fehlversuche. Gesperrt für noch ${mins} Minute${mins === 1 ? '' : 'n'}.`;
}

/** Mappt ein operator_login-Ergebnis ({ok:false,error,…}) auf einen
 *  deutschen, freundlichen Fehlertext fürs PIN-Overlay. */
export function operatorErrorMessage(res, nowMs) {
  const err = res && res.error;
  switch (err) {
    case 'PIN_FALSCH': {
      const left = (res.max_attempts != null && res.attempts != null)
        ? Math.max(0, Number(res.max_attempts) - Number(res.attempts))
        : null;
      return left != null
        ? `Falsche PIN. Noch ${left} Versuch${left === 1 ? '' : 'e'}.`
        : 'Falsche PIN.';
    }
    case 'PIN_GESPERRT':
      return formatLockMessage(res.locked_until, nowMs);
    case 'KEINE_PIN':
      return 'Für diesen Mitarbeiter wurde noch keine PIN vergeben. Bitte zuerst in den Team-Einstellungen setzen.';
    case 'GERAET_UNBEKANNT':
      return 'Dieses Gerät ist nicht mehr registriert. Bitte das Gerät neu benennen.';
    case 'OPERATOR_UNBEKANNT':
      return 'Mitarbeiter nicht gefunden oder inaktiv.';
    case 'KEIN_TENANT':
      return 'Keine aktive Anmeldung. Bitte neu einloggen.';
    default:
      return 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.';
  }
}

/* -------------------------------------------------------------------------- */
/* State-Getter                                                                */
/* -------------------------------------------------------------------------- */

/** Stabile, persistente client-UUID des Geräts (localStorage). Erzeugt sie
 *  beim ersten Aufruf. ⚠️ localStorage-Verlust = Gerät neu benennen. */
export function getDeviceId() {
  if (_deviceId) return _deviceId;
  let id = null;
  try { id = localStorage.getItem(DEVICE_ID_KEY); } catch (_) { /* ignore */ }
  if (!id) {
    id = _genUuid();
    try { localStorage.setItem(DEVICE_ID_KEY, id); } catch (_) { /* ignore */ }
  }
  _deviceId = id;
  return id;
}

export function getOperator() { return _operator ? { ..._operator } : null; }
export function isOperatorActive() { return !!_operator; }

/** Die aktive operator_session_id (oder null). Wird von den Schreibpfaden
 *  (Storage-Adapter + RPCs) mitgegeben, damit der BEFORE-Trigger den Operator
 *  serverseitig validieren + stempeln kann (S127 Phase C). */
export function getActiveSessionId() { return _operator ? _operator.sessionId : null; }
export function getDevice() { return _device ? { ..._device } : null; }
export function isDeviceRegistered() { return !!_device; }

// S222 Cut 2 — `getIdleTimeoutMs()` entfernt: der Idle-Timeout hängt ab jetzt an
// der ROLLE des aktiven Operators (settings.operatorRoleTimeouts, pro Rolle im
// Team-Tab), NICHT mehr am Gerät. Die Berechnung lebt in operator-ui.js
// (`_currentTimeoutMs()` → core/operator-roles.roleIdleTimeoutMinutes). Die
// Spalte `devices.idle_timeout_minutes` bleibt vorerst (tot) — kein DB-Touch.

/* -------------------------------------------------------------------------- */
/* DB-Pfade                                                                    */
/* -------------------------------------------------------------------------- */

/** Lädt die Geräte-Zeile (devices) für die eigene device_id. */
export async function hydrateDevice() {
  const supa = getSupa();
  if (!supa) return null;
  const id = getDeviceId();
  try {
    const { data, error } = await supa
      .from('devices')
      .select('id,label,idle_timeout_minutes')
      .eq('id', id)
      .maybeSingle();
    if (error) { console.warn('[operator] hydrateDevice:', error.message); _device = null; return null; }
    _device = data || null;
    return _device;
  } catch (e) {
    console.warn('[operator] hydrateDevice threw:', e && e.message);
    _device = null;
    return null;
  }
}

/** Lädt die aktive operator_sessions-Zeile des Geräts + den zugehörigen User
 *  → Operator-State. Überlebt Reload + ist multi-tab-konsistent. */
/** S167 Multi-Role: lädt `users.roles` defensiv. Existiert die Spalte (noch)
 *  nicht (Migration phase_127 nicht appliziert), bleibt es beim Fallback
 *  [primaryRole] — NIE ein harter Fehler (Multi-Schichten-Schutz). */
async function _loadUserRoles(supa, userId, primaryRole) {
  const fallback = primaryRole ? [primaryRole] : [];
  try {
    const ru = await supa.from('users').select('roles').eq('id', userId).maybeSingle();
    if (ru && ru.data && Array.isArray(ru.data.roles) && ru.data.roles.length) return ru.data.roles;
  } catch (_) { /* Spalte fehlt o.ä. → Fallback */ }
  return fallback;
}

/* -------------------------------------------------------------------------- */
/* S259 §5 — warum der Operator nach einem Reload weg ist                       */
/* -------------------------------------------------------------------------- */
/**
 * Die Idle-Abmeldung ist GEWOLLT: `_idleTick` in `operator-ui.js` meldet den
 * Operator nach der eingestellten Leerlaufzeit ab und legt das Sperr-Overlay
 * darüber — im offenen Tab sieht man also, was passiert. Nach einem Reload
 * (F5, Absturz, Tab-Wiederherstellung) ist dieses Overlay weg, und `hydrate`
 * findet schlicht keine offene Sitzung mehr. Das Ergebnis stimmt, aber es steht
 * ohne Erklärung da — im S259-Retest las es sich wie „F5 loggt aus".
 *
 * Die Sitzung wieder aufleben zu lassen wäre der falsche Weg: sie IST beendet,
 * mit Grund und Zeitstempel, und eine wiederbelebte Sitzung stünde falsch im
 * Prüfpfad. Also erklären statt heilen — genau einmal je beendeter Sitzung.
 */
let _idleNoticeShownFor = null;

async function _explainMissingOperator(supa, deviceId) {
  try {
    const { data, error } = await supa
      .from('operator_sessions')
      .select('id,ended_at,end_reason')
      .eq('device_id', deviceId)
      .not('ended_at', 'is', null)
      .order('ended_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error || !data || data.end_reason !== 'idle') return;
    // Nur die frische Abmeldung erklären. Eine Sitzung von gestern ist keine
    // Neuigkeit — dann ist das Gerät einfach unbesetzt.
    const endedMs = Date.parse(data.ended_at || '');
    if (!Number.isFinite(endedMs) || Date.now() - endedMs > 30 * 60 * 1000) return;
    if (_idleNoticeShownFor === data.id) return;
    _idleNoticeShownFor = data.id;
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(
        'Die Operator-Sitzung wurde wegen Leerlauf beendet. Bitte neu anmelden — '
        + 'der Konto-Login besteht weiter.', 'warning');
    }
  } catch (_) { /* Erklärung ist Kür, sie darf nichts kippen */ }
}

/** Test-Hook: die Einmal-Sperre der Idle-Erklärung zurücksetzen. */
export function _resetIdleNoticeForTests() { _idleNoticeShownFor = null; }

export async function hydrateOperator() {
  const supa = getSupa();
  if (!supa) return null;
  const id = getDeviceId();
  try {
    const { data, error } = await supa
      .from('operator_sessions')
      .select('id,operator_user_id,started_at')
      .eq('device_id', id)
      .is('ended_at', null)
      .maybeSingle();
    // S222 Cut 1 (L3) — Query-FEHLER (abgelaufenes JWT nach Handy-Standby,
    // Netzfehler beim Realtime-Reconnect — der Realtime-Handler ruft
    // hydrateOperator) darf den Operator NICHT client-seitig abmelden: letzten
    // bekannten State BEHALTEN und früh raus. Sonst feuert `operator:changed(null)`
    // → das Fahrer-Terminal verlässt sich sofort (driver-terminal.js), obwohl die
    // Session serverseitig noch offen ist. `null` NUR bei ERFOLGREICHER Query ohne
    // offene Session (= bestätigter Logout/Supersede) — der Realtime-Kanal liefert
    // bei echter Beendigung ein weiteres Event → Konsistenz stellt sich selbst her.
    if (error) {
      console.warn('[operator] hydrateOperator:', error.message);
      return getOperator();
    }
    if (!data) { _setOperator(null); await _explainMissingOperator(supa, id); return null; }
    let user = {};
    try {
      const u = await supa.from('users')
        .select('name,display_initials,role')
        .eq('id', data.operator_user_id)
        .maybeSingle();
      user = (u && u.data) || {};
    } catch (_) { /* ignore — minimaler State reicht */ }
    // S167 Multi-Role: roles[] DEFENSIV nachladen. Die Spalte existiert evtl. noch
    // nicht (Migration phase_127 nicht appliziert) → der Role-Load darf dadurch NIE
    // brechen (Multi-Schichten-Schutz, sonst role='' → Cockpit degradiert auf staff,
    // wirkt wie „abgemeldet"). Fallback: [role].
    const roles = await _loadUserRoles(supa, data.operator_user_id, user.role);
    _setOperator({
      sessionId: data.id,
      userId: data.operator_user_id,
      name: user.name || '',
      initials: user.display_initials || '',
      role: user.role || '',
      roles,
      deviceId: id,
      // S237 T2: Schicht-Beginn für die Dashboard-Kachel „Meine Schicht"
      // (`started_at` wurde hier schon immer mitselektiert, nur nie übernommen).
      startedAt: data.started_at || null
    });
    return getOperator();
  } catch (e) {
    console.warn('[operator] hydrateOperator threw:', e && e.message);
    return null;
  }
}

/** Operator-Login per Name+PIN. Gibt das RPC-Status-Objekt zurück
 *  ({ok:true,…} | {ok:false,error,…}). Setzt bei Erfolg den Operator-State. */
export async function operatorLogin(userId, pin) {
  const supa = getSupa();
  if (!supa) return { ok: false, error: 'KEIN_TENANT' };
  const id = getDeviceId();
  const { data, error } = await supa.rpc('operator_login', {
    p_device_id: id, p_user_id: userId, p_pin: pin
  });
  if (error) {
    // Nur KEIN_TENANT (oder unerwartet) wirft serverseitig → kommt hier als
    // rpc-error an. Auth-Ausgänge (falsche PIN etc.) kommen als data zurück.
    console.warn('[operator] operatorLogin rpc error:', error.message);
    return { ok: false, error: 'KEIN_TENANT', _raw: error };
  }
  const res = data || { ok: false, error: 'UNBEKANNT' };
  if (res.ok) {
    // S167 Multi-Role: das operator_login-RPC liefert die primäre Rolle; die volle
    // Rollen-Menge defensiv nachladen (Spalte evtl. noch nicht migriert → [role]).
    const roles = await _loadUserRoles(supa, res.operator_user_id, res.operator_role);
    _setOperator({
      sessionId: res.session_id,
      userId: res.operator_user_id,
      name: res.operator_name || '',
      initials: res.operator_initials || '',
      role: res.operator_role || '',
      roles,
      deviceId: id,
      // S237 T2: nur übernehmen, wenn `operator_login` es liefert. KEIN
      // Client-Zeitstempel als Ersatz — der würde beim nächsten
      // hydrateOperator (Realtime auf operator_sessions feuert direkt nach
      // dem Login) gegen den DB-Wert getauscht und über die JSON-Equality in
      // `_setOperator` ein zusätzliches `operator:changed` auslösen.
      startedAt: res.started_at || null
    });
  }
  return res;
}

/** Operator-Logout (reason 'logout' default, 'idle' für Timeout). */
export async function operatorLogout(reason) {
  const supa = getSupa();
  const id = getDeviceId();
  if (supa) {
    try {
      await supa.rpc('operator_logout', { p_device_id: id, p_reason: reason === 'idle' ? 'idle' : 'logout' });
    } catch (e) {
      console.warn('[operator] operatorLogout:', e && e.message);
    }
  }
  _setOperator(null);
}

/** Geräte-Onboarding/Umbenennen. */
export async function registerDevice(label, idleTimeoutMinutes) {
  const supa = getSupa();
  if (!supa) return { ok: false, error: 'KEIN_CLIENT' };
  const id = getDeviceId();
  const { data, error } = await supa.rpc('register_device', {
    p_device_id: id,
    p_label: label,
    p_idle_timeout_minutes: Number(idleTimeoutMinutes) || 0
  });
  if (error) return { ok: false, error };
  await hydrateDevice();
  return { ok: true, data };
}

/** Realtime-Sub auf operator_sessions der EIGENEN device_id → bei jeder
 *  Änderung (Login/Wechsel/Logout, auch aus einem zweiten Tab) re-hydraten. */
export function subscribeOperatorRealtime() {
  const supa = getSupa();
  if (!supa || _realtimeChannel) return;
  const id = getDeviceId();
  try {
    _realtimeChannel = supa
      .channel('operator-sessions-' + id)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'operator_sessions', filter: `device_id=eq.${id}` },
        () => { hydrateOperator(); })
      .subscribe();
  } catch (e) {
    console.warn('[operator] realtime subscribe:', e && e.message);
  }
}

/** Nur für Tests: kompletten Modul-State zurücksetzen. */
export function _reset() {
  const supa = getSupa();
  if (_realtimeChannel && supa) { try { supa.removeChannel(_realtimeChannel); } catch (_) { /* ignore */ } }
  _deviceId = null; _device = null; _operator = null; _realtimeChannel = null;
}

/* Test-Hooks für State-Injektion (Vitest). */
export const _internals = Object.freeze({
  _setOperator,
  setDeviceForTest: (d) => { _device = d; },
  setOperatorForTest: (o) => { _operator = o; }
});

// S127 — der plain-script Storage-Adapter (public/adapters/storage.js) ist kein
// ES-Modul und kann nicht importieren; er liest die aktive Session über
// window.App.operator.getActiveSessionId(), um sie in Direkt-Writes mitzugeben.
if (typeof window !== 'undefined') {
  window.App = window.App || {};
  window.App.operator = {
    getActiveSessionId,
    getOperator,
    isOperatorActive
  };
}
